import { Grid, IconButton, TextField, Typography } from '@mui/material';
import { DeleteOutline, AddCircleOutline } from '@mui/icons-material';
import { Autocomplete } from '@mui/material';
import {
  useFindAllCompaniesQuery,
  useFindAllHardwareQuery,
  useFindAllModulesQuery,
  useFindAllOrdersQuery,
  useFindAllSoftwareQuery,
} from '../../api/api-hooks';
import { TrimmedCompany } from '../../api/api-types';
import AddItemDialog from '../../components/AddItemDialog';
import { nullStringCheck } from '../../utils/stringManipulation';
import SoftwareTypeManagementButton from '../software-types/SoftwareTypeManagementButton';
import { SoftwareSoldItemForm } from './softwareSoldTypes';
import { itemValidator } from './softwareSoldUtils';
import useLicenseItemCreator from './useLicenseItemCreator';
import useSoftwareSoldMiddleware from './useSoftwareSoldMiddleware';

interface Props {
  company: TrimmedCompany;
  serial: string | null;
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const SoftwareSoldCreatorWithDefaults = ({
  company,
  serial,
  dialogOpen,
  setDialogOpen,
}: Props) => {
  const { data: companies } = useFindAllCompaniesQuery({});

  const { data: softwareTypes } = useFindAllSoftwareQuery({});
  const { data: hardware } = useFindAllHardwareQuery({});
  const { data: orders } = useFindAllOrdersQuery({});
  const { data: allModules } = useFindAllModulesQuery({});
  const itemCreator = useLicenseItemCreator();
  const softwareSoldHandlerMiddleware = useSoftwareSoldMiddleware();

  if (
    companies === undefined ||
    softwareTypes === undefined ||
    hardware === undefined ||
    orders === undefined ||
    serial === null ||
    allModules === undefined
  )
    return null;

  const filteredOrders = orders.filter((order) => !order.completed);
  const selectedHardware = hardware.filter(
    (hardware) => hardware.serial === serial
  );
  if (selectedHardware.length < 1) return null;

  const addFormInitialState: SoftwareSoldItemForm = {
    hardware: selectedHardware[0],
    company: company,
    license: '',
    software: null,
    order: null,
    module: null,
    modulesToAdd: [],
    notes: '',
  };

  return (
    <AddItemDialog
      addModalOpen={dialogOpen}
      setAddModalOpen={setDialogOpen}
      addFormInitialState={addFormInitialState}
      itemCreator={itemCreator}
      itemValidator={itemValidator}
      formInstructions='Software type and license are required, and if modules are to be added, module type and license must be filled in.'
      handlerMiddleware={softwareSoldHandlerMiddleware}
      itemName='Software Sold'
      children={(addFormState: SoftwareSoldItemForm, handler: any) => (
        <Grid container>
          <Grid container item xs spacing={1} direction='column'>
            <Grid item>
              <Typography>
                Company:{' '}
                {addFormState.company
                  ? addFormState.company.companyName
                  : 'None selected'}
              </Typography>
            </Grid>
            <Grid item>
              <Typography>Serial: {serial}</Typography>
            </Grid>
            <Grid item>
              <Autocomplete
                onChange={(event, value) => {
                  handler(event, value, 'software');
                }}
                value={addFormState.software}
                options={softwareTypes}
                getOptionLabel={(type) => type.type}
                style={{ width: 300 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Select Software Type'
                    variant='outlined'
                  />
                )}
              />
            </Grid>
            <Grid item>
              <SoftwareTypeManagementButton />
            </Grid>
            <Grid item>
              <TextField
                margin='dense'
                name='license'
                label='License'
                fullWidth
                value={nullStringCheck(addFormState.license)}
                onChange={handler}
              />
            </Grid>
            <Grid item>
              <Autocomplete
                onChange={(event, value) => {
                  handler(event, value, 'order');
                }}
                value={addFormState.order}
                options={filteredOrders}
                getOptionLabel={(order) =>
                  order.purchaseOrderName
                    ? order.purchaseOrderName
                    : order.orderDate
                }
                style={{ width: 300 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Select Order'
                    variant='outlined'
                  />
                )}
              />
            </Grid>
          </Grid>
          {addFormState.software?.type.toLowerCase().includes('spectras') &&
            addFormState.modulesToAdd && (
              <Grid container item xs spacing={1} direction='column'>
                <Grid container item spacing={1}>
                  <Grid item style={{ marginBottom: '.7rem' }}>
                    <Typography variant='subtitle1'>Add Modules:</Typography>
                  </Grid>
                  {addFormState.modulesToAdd.map((module, idx) => (
                    <Grid item key={idx} container direction='column'>
                      <Grid
                        item
                        container
                        spacing={1}
                        alignItems='center'
                        style={{ marginLeft: '1.5rem' }}
                      >
                        <Grid item>
                          <Autocomplete
                            onChange={(event, value) => {
                              handler(event, value, `modulesToAdd${idx}`);
                            }}
                            value={module.module}
                            options={allModules}
                            getOptionLabel={(module) => module.moduleName}
                            style={{ width: 300 }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label='Select Module'
                                variant='outlined'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item>
                          <IconButton
                            aria-label='Remove module'
                            onClick={(event) =>
                              handler(event, idx, 'deleteModule')
                            }
                            size='large'
                          >
                            <DeleteOutline color='error' />
                          </IconButton>
                        </Grid>
                      </Grid>
                      <Grid item style={{ marginLeft: '1.5rem' }}>
                        <TextField
                          margin='dense'
                          name={`moduleLicense${idx}`}
                          label='License'
                          fullWidth
                          value={nullStringCheck(
                            addFormState.modulesToAdd[idx].license
                          )}
                          onChange={handler}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid item style={{ marginLeft: '3rem' }}>
                  <IconButton
                    aria-label='Add module'
                    onClick={(event: React.MouseEvent) => {
                      handler(event, null, 'newModule');
                    }}
                    size='large'
                  >
                    <AddCircleOutline color='primary' />
                  </IconButton>
                </Grid>
              </Grid>
            )}
        </Grid>
      )}
    ></AddItemDialog>
  );
};

export default SoftwareSoldCreatorWithDefaults;
